import { z } from "zod";
import { dbDateSchema } from "./openings";
import { dbTimeSchema, appointmentHistoryServiceSchema } from "./appointments";

export const customerKindSchema = z.enum(["STANDARD", "WALK_IN"]);

export const customerSchema = z
  .object({
    id: z.string(),
    salonId: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phoneNumber: z.string(),
    email: z.string().email().nullable().optional(),
    /**
     * `WALK_IN` is the business's system walk-in customer: its phone is the
     * `walk-in:` placeholder, and it is never contacted or edited.
     */
    kind: customerKindSchema.optional(),
    createdAt: z.union([z.string(), z.date()]).optional(),
    updatedAt: z.union([z.string(), z.date()]).optional(),
  })
  .passthrough();

export const createCustomerRequestSchema = z.object({
  salonId: z.string().min(1),
  userId: z.string().min(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phoneNumber: z.string().min(5),
  email: z.string().email().optional(),
});

export const createCustomerResponseSchema = z.object({
  customerId: z.string(),
  alreadyExists: z.boolean().optional(),
});

/**
 * Owner/member-authenticated: the business's walk-in customer, created on
 * first use (`POST /v1/customers/walk-in`). Operator bookings use its id with
 * an optional `guestLabel` when the guest gives no details.
 */
export const walkInCustomerRequestSchema = z.object({
  salonId: z.string().min(1),
});

export const walkInCustomerResponseSchema = z.object({
  customerId: z.string(),
});

/**
 * Public, unauthenticated create-or-find payload for the booking flow
 * (`POST /v1/customers/booking`). Unlike {@link createCustomerRequestSchema}
 * (owner/member-authenticated), this requires no auth and accepts either a
 * `phoneNumber` or an `email` (at least one). It is idempotent on
 * (salonId, phoneNumber|email): an existing customer is returned with
 * `alreadyExists: true` rather than duplicated. Rate-limited per IP and per
 * phone. This is the API-first entry point for onboarding a brand-new
 * customer before calling `POST /v1/appointments/create`.
 */
export const bookingCustomerRequestSchema = z
  .object({
    salonId: z.string().min(1),
    userId: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    phoneNumber: z.string().min(5).optional(),
    email: z.string().email().optional(),
  })
  .refine((data) => Boolean(data.phoneNumber) || Boolean(data.email), {
    message: "phoneNumber or email is required",
    path: ["phoneNumber"],
  });

export const searchCustomersQuerySchema = z.object({
  salonId: z.string().min(1),
  userId: z.string().min(1),
  phone: z.string().min(2),
});

export const searchCustomersResponseSchema = z.object({
  customers: z.array(customerSchema),
});

export const getCustomerResponseSchema = z.object({
  customer: customerSchema,
});

export const updateCustomerRequestSchema = z.object({
  customerId: z.string().min(1),
  salonId: z.string().min(1),
  userId: z.string().min(1),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  phoneNumber: z.string().min(5).optional(),
  email: z.string().email().optional().nullable(),
});

export const updateCustomerResponseSchema = z.object({
  customerId: z.string(),
});

const appointmentScheduleSchema = z
  .object({
    id: z.string(),
    /** Business schedule owning this member calendar; null for a business schedule. */
    parentScheduleId: z.string().min(1).nullable().optional(),
    title: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    /**
     * Location parking info (parent business schedule, honoring the
     * per-location first-visit toggle). Rendered on the customer manage
     * page — parking content never appears in SMS; first-visit customers
     * are pointed here by the manage-link SMS variant.
     */
    parking: z
      .object({
        description: z.string().nullable().optional(),
        link: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .passthrough();

/**
 * What the customer has already paid toward this appointment, for surfaces
 * that show them their own booking. Present only when money was actually
 * taken; absent means nothing was charged.
 *
 * `amountPaidMinor` is the EFFECTIVE charged deposit — the original charge net
 * of settled refunds and reversals (`computeEffectiveChargedDeposit`), not the
 * quoted deposit and not the raw `chargeNowAmount`. A customer who was partly
 * refunded must never be told they still hold the full amount.
 *
 * Money here is an additive field, so it follows the convention for new money
 * on this API — ISO 4217 code plus integer minor units — rather than the
 * major-unit decimals the older fields on this payload (`services[].price`)
 * carry. See `backend.instructions.md` § Doc conventions.
 */
export const customerAppointmentPaymentSchema = z.object({
  /** Uppercase ISO 4217, from `Salon.currency`, defaulting to `"USD"`. */
  currency: z.string(),
  amountPaidMinor: z.number().int().nonnegative(),
  /**
   * What is still owed at the visit (`Appointment.dueLaterAmount`), in minor
   * units. `null` when the row carries no later balance — which is not the
   * same as zero, since a fully prepaid appointment records `0`.
   */
  balanceDueMinor: z.number().int().nonnegative().nullable(),
  /**
   * The requirement the appointment was booked under. `null` on rows that
   * predate the column. `"FULL"` means the whole price was taken up front, so
   * a consumer should not render a "due at the salon" clause for it.
   */
  requirement: z.enum(["DEPOSIT", "FULL"]).nullable(),
  /** When the charge settled; `null` when the row has no settlement marker. */
  chargedAt: z.union([z.string(), z.date()]).nullable(),
});

export const customerAppointmentItemSchema = z.object({
  id: z.string(),
  startsAt: z.union([z.string(), z.date()]),
  endsAt: z.union([z.string(), z.date()]).nullable().optional(),
  status: z.string(),
  serviceTitle: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  formattedDatetime: z.string(),
  date: dbDateSchema.optional(),
  time: dbTimeSchema.optional(),
  datetime: z.union([z.string(), z.date()]).optional(),
  phoneNumber: z.string().optional(),
  salonPhoneNumber: z.string().optional(),
  memberPhoneNumber: z.string().optional(),
  // Explicitly public TeamMember profile phone. Unlike memberPhoneNumber,
  // this field never falls back to the legacy User phone.
  publicMemberPhoneNumber: z.string().optional(),
  userId: z.string().optional(),
  userName: z.string().nullable().optional(),
  teamMemberId: z.string(),
  teamMemberName: z.string().nullable().optional(),
  schedule: appointmentScheduleSchema.nullable().optional(),
  services: z
    .array(appointmentHistoryServiceSchema)
    .optional()
    .transform((value) => (value && value.length > 0 ? value : undefined)),
  createdAt: z.union([z.string(), z.date()]).optional(),
  payment: customerAppointmentPaymentSchema.optional(),
});

export const listCustomerAppointmentsResponseSchema = z.object({
  items: z.array(customerAppointmentItemSchema),
});

export const customerPaymentMethodSchema = z
  .object({
    id: z.string(),
    brand: z.string().optional(),
    last4: z.string().optional(),
    expMonth: z.number().optional(),
    expYear: z.number().optional(),
  })
  .passthrough();

export const createCustomerPaymentMethodRequestSchema = z.object({
  customerId: z.string().min(1),
  paymentId: z.string().min(1),
});

/** Optional member context for public booking; the API validates its business. */
export const createBookingPaymentMethodRequestSchema =
  createCustomerPaymentMethodRequestSchema.extend({
    teamMemberId: z.string().min(1).optional(),
  });
export type CreateBookingPaymentMethodRequest = z.infer<
  typeof createBookingPaymentMethodRequestSchema
>;

export const createCustomerPaymentMethodResponseSchema = z.object({
  success: z.boolean(),
});

export const getCustomerPaymentMethodResponseSchema = z.object({
  paymentMethod: customerPaymentMethodSchema.nullable(),
});

export type Customer = z.infer<typeof customerSchema>;
export type CreateCustomerRequest = z.infer<typeof createCustomerRequestSchema>;
export type CreateCustomerResponse = z.infer<
  typeof createCustomerResponseSchema
>;
export type CustomerKind = z.infer<typeof customerKindSchema>;
export type WalkInCustomerRequest = z.infer<typeof walkInCustomerRequestSchema>;
export type WalkInCustomerResponse = z.infer<
  typeof walkInCustomerResponseSchema
>;
export type SearchCustomersQuery = z.infer<typeof searchCustomersQuerySchema>;
export type SearchCustomersResponse = z.infer<
  typeof searchCustomersResponseSchema
>;
export type GetCustomerResponse = z.infer<typeof getCustomerResponseSchema>;
export type UpdateCustomerRequest = z.infer<typeof updateCustomerRequestSchema>;
export type UpdateCustomerResponse = z.infer<
  typeof updateCustomerResponseSchema
>;
export type CustomerAppointmentPayment = z.infer<
  typeof customerAppointmentPaymentSchema
>;
export type CustomerAppointmentItem = z.infer<
  typeof customerAppointmentItemSchema
>;
export type ListCustomerAppointmentsResponse = z.infer<
  typeof listCustomerAppointmentsResponseSchema
>;
export type CustomerPaymentMethod = z.infer<typeof customerPaymentMethodSchema>;
export type CreateCustomerPaymentMethodRequest = z.infer<
  typeof createCustomerPaymentMethodRequestSchema
>;
export type CreateCustomerPaymentMethodResponse = z.infer<
  typeof createCustomerPaymentMethodResponseSchema
>;
export type GetCustomerPaymentMethodResponse = z.infer<
  typeof getCustomerPaymentMethodResponseSchema
>;

// ── Customer Tags ─────────────────────────────────────────

export const customerTagKindSchema = z.enum(["BLOCK", "DEPOSIT"]);
export type CustomerTagKind = z.infer<typeof customerTagKindSchema>;

export const customerTagSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  salonId: z.string(),
  kind: customerTagKindSchema,
  /** Set for business-wide tags; null for staff-scope tags. */
  businessOwnerId: z.string().nullable(),
  /** Set for staff-scope tags; null for business-wide tags. */
  teamMemberId: z.string().nullable(),
  /** Optional display name of the team member (staff-scope tags). */
  teamMemberName: z.string().nullable().optional(),
  createdByUserId: z.string().nullable(),
  createdByTeamMemberId: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});
export type CustomerTag = z.infer<typeof customerTagSchema>;

/** Resolved gate for a customer at a given booking context. */
export const customerGateSchema = z.object({
  block: z.boolean(),
  depositRequired: z.boolean(),
  source: z.enum(["BUSINESS", "STAFF", "NONE"]),
});
export type CustomerGate = z.infer<typeof customerGateSchema>;

// List tags
export const listCustomerTagsResponseSchema = z.object({
  tags: z.array(customerTagSchema),
  gate: customerGateSchema,
  canRequireCustomerDeposits: z.boolean().optional(),
});
export type ListCustomerTagsResponse = z.infer<
  typeof listCustomerTagsResponseSchema
>;

// Create tag (dashboard — can set any scope)
export const createCustomerTagRequestSchema = z.object({
  kind: customerTagKindSchema,
  scope: z.enum(["BUSINESS", "STAFF"]),
  /** Required when scope = "STAFF". */
  teamMemberId: z.string().optional(),
  reason: z.string().max(500).optional(),
});
export type CreateCustomerTagRequest = z.infer<
  typeof createCustomerTagRequestSchema
>;

export const createCustomerTagResponseSchema = z.object({
  tag: customerTagSchema,
  gate: customerGateSchema,
});
export type CreateCustomerTagResponse = z.infer<
  typeof createCustomerTagResponseSchema
>;

// Delete tag response
export const deleteCustomerTagResponseSchema = z.object({
  gate: customerGateSchema,
});
export type DeleteCustomerTagResponse = z.infer<
  typeof deleteCustomerTagResponseSchema
>;

// ── Public booking gate ────────────────────────────────────────────────────

/** Request body for the public POST /v1/booking/customer-gate endpoint. */
export const bookingCustomerGateRequestSchema = z.object({
  customerId: z.string().min(1),
  salonId: z.string().min(1),
  teamMemberId: z.string().optional(),
});
export type BookingCustomerGateRequest = z.infer<
  typeof bookingCustomerGateRequestSchema
>;

export const bookingCustomerGateResponseSchema = z.object({
  gate: customerGateSchema,
});
export type BookingCustomerGateResponse = z.infer<
  typeof bookingCustomerGateResponseSchema
>;

// ── Customer AI summary ─────────────────────────────────────────────────────

/**
 * Deterministic visit metrics behind the per-client AI summary. Amounts are
 * major-unit USD service prices (tips excluded). `summary` on the response is
 * null when the AI provider is unavailable — metrics are always present.
 */
export const customerAiSummaryMetricsSchema = z.object({
  completedVisits: z.number(),
  firstVisitAt: z.string().nullable(),
  lastVisitAt: z.string().nullable(),
  avgGapDays: z.number().nullable(),
  avgSpend: z.number().nullable(),
  totalSpent: z.number().nullable(),
  daysSinceLastVisit: z.number().nullable(),
  upcomingCount: z.number(),
  canceledCount: z.number(),
  noShowCount: z.number(),
});
export type CustomerAiSummaryMetrics = z.infer<
  typeof customerAiSummaryMetricsSchema
>;

export const customerAiSummaryResponseSchema = z.object({
  summary: z.string().nullable(),
  metrics: customerAiSummaryMetricsSchema,
});
export type CustomerAiSummaryResponse = z.infer<
  typeof customerAiSummaryResponseSchema
>;
